/**
 * 017: structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/order/order-list/columns.tsx
 *
 * Only the data, columns and labels are ours. The screen-to-template mapping is
 * specs/017-isomorphic-theme-only/contracts/screen-map.md.
 */
import type { ColumnDef } from "@tanstack/react-table";
import { Link } from "react-router-dom";
import { Badge, Flex } from "rizzui";
import { PiEyeDuotone as EyeIcon } from "react-icons/pi";
import { ComplianceStatusBadge } from "@steelyard/theme";
import { RowActions } from "../../../components/table/row-actions";
import { syncItemPath } from "../../../lib/paths";
import {
  formatMoment,
  isPastLatencyFloor,
  LATENCY_FLOOR_MINUTES,
  queueBadgeState,
  syncItemLabel,
  type SyncQueueItem,
} from "../../../lib/services/tallySync";

/** What kind of record it is, as a colour — not its status, which stays `ComplianceStatusBadge`'s. */
const TYPE_COLOR: Record<string, "info" | "secondary" | "success"> = {
  invoice: "info",
  credit_note: "secondary",
  settlement: "success",
  customer: "success",
};

/**
 * Sizes are the template's own values: 300 for the identity cell, 150 for a short field, 200 for a
 * date, 140 for status, 130 for actions. No selection column — no bulk action exists on this list
 * or anywhere in `apps/admin` today.
 */
export const queueColumns = (): ColumnDef<SyncQueueItem>[] => [
  {
    id: "entityReference",
    header: "Reference",
    accessorFn: (item) => syncItemLabel(item) ?? "",
    size: 260,
    cell: ({ row }) => (
      <Flex direction="col" align="start" gap="0">
        {row.original.entityReference ? (
          <Link to={syncItemPath(row.original.itemNumber || row.original.itemId)} className="font-semibold text-gray-900 hover:underline">
            {syncItemLabel(row.original) ?? "—"}
          </Link>
        ) : (
          <span className="text-muted-foreground">not yet referenced</span>
        )}
      </Flex>
    ),
  },
  {
    id: "entityType",
    header: "Type",
    accessorKey: "entityType",
    size: 140,
    cell: ({ row }) => (
      <Badge variant="flat" color={TYPE_COLOR[row.original.entityType] ?? "secondary"} rounded="pill" className="font-mono text-xs">
        {row.original.entityType}
      </Badge>
    ),
  },
  {
    id: "status",
    header: "Status",
    accessorKey: "status",
    size: 160,
    cell: ({ row }) => {
      const badge = queueBadgeState(row.original.status);
      return badge ? <ComplianceStatusBadge subject="tally" state={badge} /> : <span className="font-mono text-xs">{row.original.status}</span>;
    },
  },
  {
    // Verbatim, only clamped to two lines here — the full text is the row's own detail page.
    id: "lastError",
    header: "Tally's response",
    accessorKey: "lastError",
    size: 280,
    cell: ({ row }) =>
      row.original.lastError ? (
        <span title={row.original.lastError} className="line-clamp-2 text-xs text-red-dark">
          {row.original.lastError}
        </span>
      ) : (
        <span className="text-xs text-gray-500">No error reported</span>
      ),
  },
  {
    id: "attempts",
    header: "Attempts",
    // NaN (not reported) sorts last rather than as a number.
    accessorFn: (item) => (Number.isNaN(item.attempts) ? undefined : item.attempts),
    sortUndefined: "last",
    size: 110,
    meta: { align: "right" },
    cell: ({ row }) =>
      Number.isNaN(row.original.attempts) ? (
        <span className="text-muted-foreground">not reported</span>
      ) : (
        <span className="font-semibold tabular-nums">{row.original.attempts}</span>
      ),
  },
  {
    // T018 (FR-012, GAP-1 closed 2026-09-14): `queuedAt` is now a real, API-supplied timestamp —
    // flags a row past §19's 15-minute floor rather than just showing when it was raised.
    id: "queuedAt",
    header: "Queued (UTC)",
    accessorKey: "queuedAt",
    size: 200,
    cell: ({ row }) => {
      const { queuedAt } = row.original;
      if (!queuedAt) return <span className="text-muted-foreground">not reported</span>;
      return (
        <Flex direction="col" align="start" gap="1">
          <span className="text-muted-foreground">{formatMoment(queuedAt)}</span>
          {isPastLatencyFloor(queuedAt) ? (
            <Badge variant="flat" color="danger" rounded="pill" className="text-xs">
              Over {LATENCY_FLOOR_MINUTES}m
            </Badge>
          ) : null}
        </Flex>
      );
    },
  },
  {
    id: "lastAttemptAt",
    header: "Last attempt (UTC)",
    accessorKey: "lastAttemptAt",
    size: 200,
    cell: ({ row }) => <span className="text-muted-foreground">{formatMoment(row.original.lastAttemptAt)}</span>,
  },
  // No delete: no endpoint removes a queue item, and the queue detail page is where it's actioned
  // (retried), not deleted. See TableRowActionGroup's own `onDelete` for the pattern this replaces.
  {
    id: "actions",
    header: "",
    enableSorting: false,
    size: 90,
    cell: ({ row }) => (
      <RowActions
        name={syncItemLabel(row.original) ?? "sync record"}
        primary={{ label: "View record", icon: EyeIcon, to: syncItemPath(row.original.itemNumber || row.original.itemId) }}
      />
    ),
  },
];
